import * as yup from 'yup';
import { ROLREVENDEDOR } from './constants';

/**
 * Validador para un campo de importe.
 *
 * Es un `yup.number()` que además acepta la coma decimal española ('57,5' -> 57.5).
 * Los formatos ambiguos con separador de miles ('1.234,56', '1,234.56') se RECHAZAN
 * a propósito, en vez de adivinar el valor. Ver `parseImporte` en `money.ts`.
 */
export const yupImporte = () =>
  yup
    .number()
    .transform((value, originalValue) =>
      typeof originalValue === 'string' && originalValue.includes(',')
        ? Number(originalValue.trim().replace(',', '.'))
        : value,
    );

export const validationSchemaUser = () => {
  return yup.object({
    name: yup
      .string('El nombre es obligatorio')
      .min(3, 'El nombre debe tener un mínimo de 3 caracteres')
      .required('El nombre es obligatorio'),
    surname: yup
      .string('Los apellidos son obligatorios')
      .min(3, 'Los apellidos deben tener un mínimo de 3 caracteres')
      .test('surname-required-if-persona-fisica', 'Los apellidos son obligatorios', function (value) {
        const { EsEmpresa, rol } = this.parent;
        if (rol === ROLREVENDEDOR && !EsEmpresa) {
          return value && value.trim().length > 0;
        }
        return true;
      }),
    nombrePersonaFisica: yup
      .string()
      .test(
        'nombre-persona-fisica-required-if-agencia-no-empresa',
        'El nombre de persona física es obligatorio',
        function (value) {
          const { EsEmpresa, rol, tipoUsuario } = this.parent;
          const esAgencia = tipoUsuario === 2;
          if (rol === ROLREVENDEDOR && esAgencia && !EsEmpresa) {
            return value && value.trim().length > 0;
          }
          return true;
        },
      ),
    email: yup.string('El email es obligatorio').email('El email no es válido').required('El email es obligatorio'),
    telefono: yup.string('El teléfono es obligatorio').required('El teléfono es obligatorio'),
    date: yup.date().nullable(),
    password: yup
      .string('La contraseña es obligatoria')
      .required('La contraseña es obligatoria')
      .min(8, 'La contraseña debe tener un mínimo de 8 caracteres'),
    rol: yup.number().required('El rol es requerido'),
    tipoUsuario: yup.number().required(),
    razonSocial: yup
      .string()
      .test('razon-social-required-if-agencia-empresa', 'La razón social es obligatoria', function (value) {
        const { EsEmpresa } = this.parent;
        if (EsEmpresa) {
          return value && value.trim().length > 0;
        }
        return true;
      }),
    NIF: requiredStringForRevendedor('nif', 'El NIF es obligatorio'),
    Direccion: requiredStringForRevendedor('direccion', 'La dirección es obligatoria'),
    FkPaises: requiredNumberForRevendedor('pais', 'El país es obligatorio'),
    CodigoPostal: requiredStringForRevendedor('codigoPostal', 'El código postal es obligatorio'),
    Municipio: requiredStringForRevendedor('municipio', 'El municipio es obligatorio'),
    Provincia: requiredStringForRevendedor('provincia', 'La provincia es obligatoria'),
  });
};

const requiredStringForRevendedor = (fieldName, message) => {
  return yup.string().test(`${fieldName}-required-for-revendedor`, message, function (value) {
    const { rol } = this.parent;
    if (rol === ROLREVENDEDOR) {
      return value && value.trim().length > 0;
    }
    return true;
  });
};

const requiredNumberForRevendedor = (fieldName, message) => {
  return yup.number().test(`${fieldName}-required-for-revendedor`, message, function (value) {
    const { rol } = this.parent;
    if (rol === ROLREVENDEDOR) {
      return value !== undefined && value !== null && value > 0;
    }
    return true;
  });
};

export const validationSchemaUserEdit = (esRevendedor = false, esAgencia = false) => {
  return yup.object({
    name: yup
      .string('El nombre es obligatorio')
      .min(3, 'El nombre debe tener un mínimo de 3 caracteres')
      .required('El nombre es obligatorio'),

    email: yup.string('El email es obligatorio').email('El email no es válido').required('El email es obligatorio'),

    telefono: yup.string('El teléfono es obligatorio').required('El teléfono es obligatorio'),

    date: yup.date().nullable(),

    surname: yup
      .string()
      .test('surname-required-if-persona-fisica', 'Los apellidos son obligatorios', function (value) {
        const { EsEmpresa } = this.parent;
        if (esRevendedor && !EsEmpresa) {
          return value && value.trim().length >= 3;
        }
        return true;
      }),

    razonSocial: yup
      .string()
      .test('razon-social-required-if-agencia-empresa', 'La razón social es obligatoria', function (value) {
        const { EsEmpresa } = this.parent;
        if (esRevendedor && EsEmpresa) {
          return value && value.trim().length > 0;
        }
        return true;
      }),

    nombrePersonaFisica: yup
      .string()
      .test('nombre-persona-fisica-required-if-agencia-no-empresa', 'El nombre es obligatorio', function (value) {
        const { EsEmpresa } = this.parent;
        if (esRevendedor && esAgencia && !EsEmpresa) {
          return value && value.trim().length >= 3;
        }
        return true;
      }),

    NIF: esRevendedor
      ? yup.string().test('nif-required', 'El NIF es obligatorio', value => value && value.trim().length > 0)
      : yup.string().optional(),

    Direccion: esRevendedor
      ? yup.string().nullable().required('La dirección es obligatoria')
      : yup.string().nullable().notRequired(),

    FkPaises: esRevendedor
      ? yup.number().nullable().required('El país es obligatorio')
      : yup.number().nullable().notRequired(),

    CodigoPostal: esRevendedor
      ? yup.string().nullable().required('El código postal es obligatorio')
      : yup.string().nullable().notRequired(),

    Municipio: esRevendedor
      ? yup.string().nullable().required('El municipio es obligatorio')
      : yup.string().nullable().notRequired(),

    Provincia: esRevendedor
      ? yup.string().nullable().required('La provincia es obligatoria')
      : yup.string().nullable().notRequired(),
  });
};

export const validationSchemaSupplier = () => {
  return yup.object({
    Nombre: yup
      .string('El nombre es obligatorio')
      .min(3, 'El nombre debe tener un mínimo de 3 caracteres')
      .required('El nombre es obligatorio'),
    Email: yup.string('El email es obligatorio').email('El email no es válido').required('El email es obligatorio'),
    Telefono: yup.number().optional().positive().integer('Debe ser un entero'),
    date: yup.date().nullable(),
    PaginaWeb: yup.string().optional(),
    /*nif: yup.string().required('El NIF es requerido'),
        codigo: yup.string().required('El código es requerido'),
        ciudad: yup.string().required('La ciudad es requerida'),
        localizacion: yup.string().required('La localización es requerida')*/
  });
};

export const validationSchemaExchangeRate = () => {
  return yup.object({
    proveedor: yup.string('Seleccione el proveedor').required('Seleccione el proveedor'),
    tipo_servicio: yup.string().required('Seleccione el tipo de servicio'),
    cambioEuros: yup.number().required('Seleccione el cambio en euros'),
    cambioDolares: yup.number().required('Seleccione el cambio en dólares'),
  });
};

export const validationSchemaSettings = () => {
  return yup.object({
    NombreEmpresa: yup.string('Indique el nombre de la empresa').required('Indique el nombre de la empresa'),
    RazonSocial: yup.string('La razón social es requerida').required('La razón social es requerida'),
  });
};

export const validationSchemaProfile = () => {
  return yup.object({
    name: yup.string('El nombre es obligatorio').required('El nombre es obligatorio'),
    surname: yup.string('Los apellidos son obligatorios').required('Los apellidos obligatorios'),
    email: yup.string('El email es obligatorio').required('El email es obligatorio'),
    phone: yup.string('El teléfono es obligatorio').required('El teléfono es obligatorio'),
  });
};

export const validationSchemaSupplierService = () => {
  return yup.object({
    proveedor: yup.string('Seleccione el proveedor').required('Seleccione el proveedor'),
    tipo_servicio: yup.string('Seleccione el tipo de servicio').required('Seleccione el tipo de servicio'),
    Nombre: yup
      .string('Indique el nombre del servicio')
      .min(3, 'El nombre del servicio debe tener un mínimo de 3 caracteres')
      .required('Indique el nombre del servicio'),
    CosteDolares: yupImporte().required('El coste en dólares es obligatorio'),
    PrecioVentaEuros: yupImporte().required('El precio de venta en euros es obligatorio'),
    PaginaWeb: yup.string().optional(),
    Observaciones: yup.string().optional(),
  });
};

export const validationSchemaSupplierServiceEdit = () => {
  return yup.object({
    FkProveedores: yup.string('Seleccione el proveedor').required('Seleccione el proveedor'),
    FkTiposServicio: yup.string('Seleccione el tipo de servicio').required('Seleccione el tipo de servicio'),
    Nombre: yup
      .string('Seleccione el nombre de servicio')
      .min(3, 'El nombre del servicio debe tener un mínimo de 3 caracteres')
      .required('Seleccione el nombre de servicio'),
    CosteDolares: yupImporte().required('El coste en dólares es obligatorio'),
    PrecioVentaEuros: yupImporte().required('El precio de venta en euros es obligatorio'),
    PaginaWeb: yup.string().optional(),
    Observaciones: yup.string().optional(),
  });
};

export const validationSchemaEditResellerService = () => {
  return yup.object({
    servicio: yup.number('Seleccione el servicio').required('Seleccione el servicio'),
    precioVenta: yupImporte().required('Precio venta es obligatorio'),
    moneda: yup.string().required('Moneda es obligatorio'),
    //importeReserva: yup.number().moreThan(0, 'El importe de reserva debe ser positivo').required('reserva es obligatorio'),
    // observaciones: yup.string().optional(),
  });
};

export const validationSchemaSupplpierToCoordinator = () => {
  return yup.object({
    proveedor: yup.string().required(),
  });
};

export const validationSchemaCustomer = () => {
  return yup.object({
    Nombre: yup.string('Este campo es obligatorio').required('El nombre es obligatorio'),
    Apellidos: yup
      .string()
      .test('apellidos-required-if-not-empresa', 'Los apellidos son obligatorios', function (value) {
        const { EsEmpresa } = this.parent;
        if (EsEmpresa) {
          return true;
        }
        return value && value.trim().length > 0;
      }),
    Email: yup.string().email('Formato de email incorrecto').notRequired().nullable(),
    Telefono: yup
      .string('El teléfono es obligatorio')
      .required('El teléfono es obligatorio')
      .matches(/^\+/, 'El teléfono debe comenzar por + (prefijo internacional)'),
    NumeroIdentificacionFiscal: yup
      .string()
      .test('nif-required-if-not-temporal', 'El NIF es obligatorio', function (value) {
        const { ClienteTemporal } = this.parent;
        if (ClienteTemporal) {
          return true;
        }
        return value && value.trim().length > 0;
      }),
    date: yup.date().notRequired().nullable(),
    Observaciones: yup.string().notRequired().nullable(),
    FkPaises: yup
      .number()
      .test('pais-required-if-not-temporal', 'El país es obligatorio', function (value) {
        const { ClienteTemporal } = this.parent;
        if (ClienteTemporal) {
          return true;
        }
        return value !== undefined && value !== null && value > 0;
      })
      .nullable(),
    Direccion: yup
      .string()
      .test('direccion-required-if-not-temporal', 'La dirección es obligatoria', function (value) {
        const { ClienteTemporal } = this.parent;
        if (ClienteTemporal) {
          return true;
        }
        return value && value.trim().length > 0;
      })
      .nullable(),
    CodigoPostal: yup
      .string()
      .test('codigo-postal-required-if-not-temporal', 'El código postal es obligatorio', function (value) {
        const { ClienteTemporal } = this.parent;
        if (ClienteTemporal) {
          return true;
        }
        return value && value.trim().length > 0;
      })
      .nullable(),
    Municipio: yup
      .string()
      .test('municipio-required-if-temporal', 'El municipio es obligatorio', function (value) {
        const { ClienteTemporal } = this.parent;
        if (ClienteTemporal) {
          return true;
        }
        return value && value.trim().length > 0;
      })
      .nullable(),

    Provincia: yup
      .string()
      .test('provincia-required-if-temporal', 'La provincia es obligatoria', function (value) {
        const { ClienteTemporal } = this.parent;
        if (ClienteTemporal) {
          return true;
        }
        return value && value.trim().length > 0;
      })
      .nullable(),
    Numero: yup.string().notRequired().nullable(),
    Piso: yup.string().notRequired().nullable(),
    DireccionComplementaria: yup.string().notRequired().nullable(),
  });
};

export const validationSchemaComisionista = () => {
  return yup.object({
    Nombre: yup.string('El nombre es obligatorio').required('El nombre es obligatorio'),
    Email: yup.string().email('Formato de email incorrecto').notRequired().nullable(),
    Telefono: yup.string().notRequired().nullable(),
    Observaciones: yup.string().notRequired().nullable(),
  });
};

export const validationSchemaOrder = () => {
  return yup.object({
    FechaVenta: yup.date('Fecha venta es requerido').nullable(),
    HotelCliente: yup.string('Hotel cliente es requerido').optional('Hotel cliente es requerido'),
    PorcDescuento: yup.number('Porcentaje de descuento es requerido').optional('Porcentaje de descuento es requerido'),
    PresupuestoEnviado: yup
      .boolean('Es requerido indicar si se ha enviado el presupuesto')
      .optional('Es requerido indicar si se ha enviado el presupuesto'),
    FechaEnvioPresupuesto: yup.date('Formato invalido').nullable(),
    ReservaPagada: yup
      .boolean('Es requerido indicar si se ha pagado la reserva')
      .optional('Es requerido indicar si se ha pagado la reserva'),
    FechaPagoReserva: yup.date('Fecha pago es requerida').nullable(),
    VoucherEnviado: yup
      .boolean('Es requerido indicar si se ha enviado el voucher')
      .optional('Es requerido indicar si se ha enviado el voucher'),
    FechaEnvioVoucher: yup.date('Fecha envío voucher es requerida').nullable(),
    FechaFactura: yup.date('Fecha factura es requerido').nullable(),
    ImporteFactura: yupImporte().optional('Importe factura es requerido'),
    Observaciones: yup.string().nullable(),
    InformacionCambioDivisa: yup.string().nullable(),
  });
};

export const validationSchemaBudget = () => {
  return yup.object({
    FechaPresupuesto: yup.date('Fecha presupuesto es requerido').required('Fecha presupuesto es requerido'),
    FechaLimite: yup
      .date('Fecha limite es requerido')
      .required('Fecha limite es requerido')
      .min(yup.ref('FechaPresupuesto'), 'Fecha límite debe ser mayor que la fecha de presupuesto'),
    HotelCliente: yup.string().optional(), //('Hotel cliente es requerido').required('Hotel cliente es requerido'),
    Observaciones: yup.string().optional(),
    Cliente: yup.number().required('Cliente es requerido'),
    Idioma: yup.string().optional(),
    Moneda: yup.string().optional(),
  });
};

export const validationSchemaConfirmLine = () => {
  return yup.object({
    FechaRecogida: yup.date('Fecha recogida es requerido').required('Debe indicar la fecha de la actividad'), //.optional().nullable(),
    CodigoReservaProveedor: yup.string().nullable(),
    ObservacionesRecogida: yup.string().nullable(),
  });
};

export const validationSchemaEditLine = () => {
  return yup.object({
    FkProveedores: yup.number().required('Proveedor es requerido'),
    FkServiciosProveedores: yup.number().required('Servicio es requerido'),
    ReservaLineaEuros: yupImporte().required('Reserva es requerida'),
    InSituLineaDolares: yupImporte().required('Coste es requerido'),
    Unidades: yup.number().required('Personas es requerido'),
    Concepto: yup.string().optional(),
    //Observaciones: yup.string().optional(),
    FechaRecogida: yup.date().nullable(),
    PorcDescuento: yup
      .number('Debe ser un entero')
      .integer('Debe ser un entero')
      .min(0, 'El descuento no puede ser negativo')
      .max(100, 'El descuento no puede ser superior al 100%')
      .optional(),
  });
};

export const validationSchemaEditLineReseller = () => {
  return yup.object({
    FkProveedores: yup.number().required('Proveedor es requerido'),
    FkServiciosProveedores: yup.number().required('Servicio es requerido'),
    ReventaLineaEuros: yupImporte()
      .required('El importe de reventa es requerido')
      .min(0, 'El margen no puede ser negativo'),
    Unidades: yup.number().required('Personas es requerido'),
    Concepto: yup.string().optional(),
    //Observaciones: yup.string().optional(),
    FechaRecogida: yup.date().nullable(),
    //PorcDescuento: yup.number('Debe ser un entero').integer('Debe ser un entero').min(0, 'El descuento no puede ser negativo').max(100, 'El descuento no puede ser superior al 100%').optional(),
  });
};

export const validationSchemaEditFreeLine = () => {
  return yup.object({
    FkProveedores: yup.number().required('Proveedor es requerido'),
    FkTipoServicio: yup.number().required('Tipo de servicio es requerido'),
    ReservaLineaEuros: yupImporte().required('Reserva es requerida'),
    InSituLineaDolares: yupImporte().required('Coste es requerido'),
    Unidades: yup.number().required('Personas es requerido'),
    Concepto: yup.string().required(),
    Observaciones: yup.string().optional(),
  });
};
